import { ChangeEvent, useState, useEffect } from "react";
import { Typography, Box, IconButton, Button, TextField } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { MinusIcon, PlusIcon } from "@phosphor-icons/react";
import { useCart } from "@ecommerce/store";
import { useSnackbar } from "notistack";
import { BUTTON_STYLES } from "../../styles/buttonStyles";

const QuantitySelectorStyles: any = makeStyles(() =>
    createStyles({
        input: {
            flexGrow: 1,
            minWidth: "60px",
            "& .MuiInputBase-input": {
                fontFamily: "'Rubik', sans-serif",
                fontSize: "16px",
                py: 0.5,
                textAlign: "center",
                MozAppearance: "textfield",
                "&::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                },
                "&::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                },
            },
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    border: "none",
                },
                "&:hover fieldset": {
                    border: "none",
                },
                "&.Mui-focused fieldset": {
                    border: "none",
                },
            },
        },
    }),
);

interface QuantitySelectorProps {
    productId: string;
    variantId?: string;
    productName: string;
    productPrice: number;
    productImage: string;
    quantity: number;
    stock: number;
    onQuantityChange: (newQuantity: number) => void;
    minQuantity?: number;
    onAfterAddToCart?: () => void;
    attributes?: { name: string; value: string }[];
}

export default function QuantitySelector({
    productId,
    variantId,
    productName,
    productPrice,
    productImage,
    quantity,
    stock,
    onQuantityChange,
    minQuantity,
    onAfterAddToCart,
    attributes,
}: QuantitySelectorProps) {
    const { addToCart } = useCart();
    const { enqueueSnackbar } = useSnackbar();
    const classes = QuantitySelectorStyles();

    const effectiveMinQuantity = minQuantity ?? 1;
    const isPurchasable = stock >= effectiveMinQuantity;

    const [inputValue, setInputValue] = useState<string>(quantity.toString());
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    useEffect(() => {
        setInputValue(quantity.toString());
    }, [quantity]);

    const handleAddToCart = async () => {
        if (!isPurchasable) {
            enqueueSnackbar(
                `No hay stock suficiente para el mínimo requerido (${effectiveMinQuantity})`,
                { variant: "warning" },
            );
            return;
        }

        if (quantity < effectiveMinQuantity) {
            enqueueSnackbar(`La cantidad mínima es ${effectiveMinQuantity}`, {
                variant: "warning",
            });
            return;
        }

        if (quantity > stock) {
            enqueueSnackbar(`No puede agregar ${quantity}, hay como máximo ${stock}`, {
                variant: "warning",
            });
            return;
        }

        // Validar que haya variante seleccionada
        if (!variantId) {
            enqueueSnackbar("Por favor selecciona una variante del producto", {
                variant: "warning",
            });
            return;
        }

        setIsAddingToCart(true);
        try {
            await addToCart({
                idProduct: productId,
                idProductVariant: variantId,
                name: productName,
                price: productPrice,
                image: productImage,
                stock,
                quantity,
                attributes: attributes || [],
            });

            enqueueSnackbar("Producto agregado al carrito exitosamente!", { variant: "success" });

            onAfterAddToCart?.();
        } catch (error) {
            console.error("No se pudo agregar el producto al carrito.", error);
            enqueueSnackbar("No se pudo agregar el producto al carrito. Intente nuevamente.", {
                variant: "error",
            });
        } finally {
            setIsAddingToCart(false);
        }
    };

    const onChangeInputQuantity = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const onBlurInputQuantity = () => {
        let parsed = Number(inputValue);

        if (!inputValue || isNaN(parsed)) {
            parsed = effectiveMinQuantity;
        }

        if (!isPurchasable) {
            parsed = effectiveMinQuantity;
        } else {
            parsed = Math.max(parsed, effectiveMinQuantity);
            parsed = Math.min(parsed, stock);
        }

        onQuantityChange(parsed);
        setInputValue(parsed.toString());
    };

    const handleIncrement = () => {
        if (!isPurchasable) return;
        const next = Math.min(quantity + 1, stock);
        onQuantityChange(next);
        setInputValue(next.toString());
    };

    const handleDecrement = () => {
        const next = Math.max(quantity - 1, effectiveMinQuantity);
        onQuantityChange(next);
        setInputValue(next.toString());
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Typography sx={{ mb: 1, fontFamily: "'Rubik', sans-serif", fontSize: "10px" }}>
                CANTIDAD
            </Typography>

            <Box sx={{ width: "50%", mb: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #e0e0e0",
                        overflow: "hidden",
                        height: "40px",
                    }}
                >
                    <IconButton
                        onClick={handleDecrement}
                        disabled={quantity <= effectiveMinQuantity}
                        sx={{
                            borderRadius: 0,
                            color: "black",
                            "&:hover": {
                                backgroundColor: "grey.100",
                            },
                        }}
                    >
                        <MinusIcon size={16} />
                    </IconButton>

                    <TextField
                        className={classes.input}
                        type="number"
                        variant="outlined"
                        value={inputValue}
                        onChange={onChangeInputQuantity}
                        onBlur={onBlurInputQuantity}
                    />

                    <IconButton
                        onClick={handleIncrement}
                        disabled={!isPurchasable || quantity >= stock}
                        sx={{
                            borderRadius: 0,
                            color: "black",
                            "&:hover": {
                                backgroundColor: "grey.100",
                            },
                        }}
                    >
                        <PlusIcon size={16} />
                    </IconButton>
                </Box>

                {/* Stock info */}
                {!!minQuantity && (
                    <Typography
                        color={"text.lighter"}
                        sx={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: "10px",
                        }}
                    >
                        {`Mínimo ${minQuantity} unidad${minQuantity > 1 ? "es" : ""}`}
                    </Typography>
                )}

                <Typography
                    sx={{
                        color: "#010101",
                        fontFamily: "'Rubik', sans-serif",
                        fontSize: "12px",
                        textAlign: "center",
                        mt: 2,
                    }}
                >
                    {stock} en stock
                </Typography>
            </Box>

            <Button
                variant="contained"
                onClick={handleAddToCart}
                disabled={!isPurchasable || !variantId || isAddingToCart}
                sx={{
                    ...BUTTON_STYLES.primary,
                    width: "100%",
                    mt: 2,
                    ...(!isPurchasable && BUTTON_STYLES.disabled),
                }}
            >
                AGREGAR AL CARRITO
            </Button>
        </Box>
    );
}
